// Team surfaces + role taxonomy per surface. Real-API TeamManagement reads the
// role descriptors; MSW team fixtures import the types from here (moved out of
// mocks/data/settings.ts, OPT-20261031-05).

export type TeamSurface = 'admin' | 'seller' | 'supply'

export type TeamMemberStatus = 'active' | 'invited' | 'inactive'

export type TeamMember = {
  id: string
  surface: TeamSurface
  org_id: string
  display_name: string
  email: string
  role: string
  status: TeamMemberStatus
  last_active_at: string | null
  invited_at: string
  initials: string
}

// ---------------------------------------------------------------------------
// Role taxonomy per surface — preview matrix surfaces these in TeamManagement.
// Schema commit Stage 4 SPEC.md Faz 6; permission semantics ratified Faz 1
// (iam-auth A-32 5-tier RBAC cross-link).
// ---------------------------------------------------------------------------

export type RoleDescriptor = {
  value: string
  label: string
  summary: string
}

const ADMIN_ROLES: RoleDescriptor[] = [
  {
    value: 'admin',
    label: 'Admin',
    summary: 'Full platform governance · Ring 3 audit access · approve payouts',
  },
  {
    value: 'ops',
    label: 'Ops',
    summary: 'Daily order workflow · assign + tag · address verification review',
  },
  {
    value: 'customer_support',
    label: 'Customer Support',
    summary: 'Read orders + customer comms · escalate replacements + holds',
  },
  {
    value: 'graphic_designer',
    label: 'Graphic Designer',
    summary: 'Personalization queue · proof generation · design asset library',
  },
  {
    value: 'shipping',
    label: 'Shipping',
    summary: 'Label batch print · carrier select · pickup scheduling',
  },
  {
    value: 'warehouse_lead',
    label: 'Warehouse Lead',
    summary: 'Inventory pool · receiving + putaway · daily outbound batch lead',
  },
]

const SELLER_ROLES: RoleDescriptor[] = [
  {
    value: 'owner',
    label: 'Owner',
    summary: 'Account owner · billing + team config · full operational scope',
  },
  {
    value: 'manager',
    label: 'Manager',
    summary: 'Daily orders + inventory + report submission · cannot manage team',
  },
  {
    value: 'customer_support',
    label: 'Customer Support',
    summary: 'Order assignments + customer message handling · tag edit · cannot finance/pricing',
  },
  {
    value: 'assistant',
    label: 'Assistant',
    summary: 'Order assignment recipient · limited edit · view-only billing',
  },
  {
    value: 'designer',
    label: 'Designer',
    summary: 'Personalization fulfillment · proof handoff · no order finance',
  },
]

const SUPPLY_ROLES: RoleDescriptor[] = [
  {
    value: 'owner',
    label: 'Owner',
    summary: 'Workshop owner · WO acceptance + invoice · full team config',
  },
  {
    value: 'production_lead',
    label: 'Production Lead',
    summary: 'Daily WO production scheduling · QC sign-off · shipment handoff',
  },
  {
    value: 'helper',
    label: 'Helper',
    summary: 'Assigned tasks only · packaging + finishing · no WO state change',
  },
]

export function rolesForSurface(surface: TeamSurface): RoleDescriptor[] {
  if (surface === 'admin') return ADMIN_ROLES
  if (surface === 'seller') return SELLER_ROLES
  return SUPPLY_ROLES
}
